import React, { ChangeEvent } from 'react';
import Joi from 'joi';

import Conditional from '../common/conditional';
import Confirm from '../themed/confirm';
import Form from '../common/form';
import Progress from '../themed/progress';

import * as models from '../../utils/models';
import * as utils from '../../utils/utilities';

import * as service from '../../services/projectService';
import * as toastService from '../../services/toastService';

class ActivateProjectForm extends Form {
	state = {
		data: { isActive: '', comment: '', rejectReason: '', oldValue: '' },
		errors: {},
		loading: false,
		progress: 0,
		showConfirmation: false,
	};

	schema = {
		isActive: Joi.string().min(2).required().label('Activate'),
		rejectReason: Joi.any().when('isActive', {
			is: 'no',
			then: Joi.string().required().label('Reason'),
			otherwise: Joi.string().allow('').label('Reason'),
		}),
		comment: Joi.any().when('isActive', {
			is: 'no',
			then: Joi.string().min(7).required().label('Comments'),
			otherwise: Joi.string().allow('').label('Comments'),
		}),
		oldValue: Joi.any().when('isActive', {
			is: 'no',
			then: Joi.string().required().label('Old Value'),
			otherwise: Joi.string().allow('').label('Old Value'),
		}),
	};

	handleActivateProject = async (): Promise<void> => {
		if (!this.props.project) return;

		const apiData = {
			...this.state.data,
			isActive: utils.matchPhrases(this.state.data.isActive, 'yes'),
		};

		await service.activateProject(
			this.props.project._id,
			apiData,
			this.handleProgressChange
		);

		window.location.reload();
	};

	handleRejectReasonChange = async (event: ChangeEvent<HTMLSelectElement>) => {
		await this.handleChange(event);

		const project: models.Project | null = this.props.project;
		if (!project) return;

		const reason = event.target.value as keyof typeof project;
		const data = { ...this.state.data };
		if (reason in project) data.oldValue = project[reason] as string;

		this.setState({ data });
	};

	handleShowConfirmation = () => {
		const showConfirmation: boolean = !this.state.showConfirmation;
		this.setState({ showConfirmation } as any);
	};

	doSubmit = (): void => {
		this.handleShowConfirmation();
	};

	postData = (): void => {
		const isActive = utils.matchPhrases(this.state.data.isActive, 'yes');
		const prefix: string = isActive ? 'Activat' : 'Reject';

		toastService.promise(this.handleActivateProject(), {
			loading: `${prefix}ing Project`,
			success: `Project ${prefix}ed`,
			error: utils.getErrorMessage,
		});
	};

	render() {
		const isNotActive = utils.matchPhrases(this.state.data.isActive, 'no');

		return (
			<React.Fragment>
				<Conditional isVisible={!this.state.showConfirmation}>
					<Progress progress={this.state.progress} />

					<form autoComplete="off" onSubmit={this.handleSubmit}>
						<p className="form-subtitle">
							Toggle the switch below to activate/reject the project. Comments
							are mandatory if you choose to reject the project
						</p>

						<div className="my-4">
							{this.renderSelect({
								label: 'Activate Project',
								placeholder: 'Do you approve this project?',
								name: 'isActive',
								options: [
									{
										id: 'yes',
										name: 'Yes, I approve',
									},
									{
										id: 'no',
										name: 'No, I do not approve',
									},
								],
							})}
						</div>

						<Conditional isVisible={isNotActive}>
							{this.renderSelect({
								name: 'rejectReason',
								label: 'Reason for Rejection',
								placeholder: 'Select Reason for Rejection',
								options: rejectionReasons,
								onChange: this.handleRejectReasonChange,
							})}
						</Conditional>

						{this.renderTextArea({
							name: 'comment',
							label: 'Comments',
							rows: '4',
							placeholder: 'Enter Your Comments (Optional)',
						})}

						<div className="d-flex justify-content-end">
							{this.renderButton('Submit')}
						</div>
					</form>
				</Conditional>

				<Conditional isVisible={this.state.showConfirmation}>
					<Confirm
						title="Confirm Action"
						message="Are you sure you want to perform this action? This action is not reversible"
						onCancel={this.handleShowConfirmation}
						onConfirm={this.postData}
					/>
				</Conditional>
			</React.Fragment>
		);
	}
}

const rejectionReasons: models.SelectOption[] = [
	{
		id: 'organisationName',
		name: 'Organization Name',
	},
	{ id: 'projectTitle', name: 'Project Title' },
	{ id: 'contactPersonName', name: 'Customer Contact Person' },
	{ id: 'facilityName', name: 'Facility' },
	{ id: 'equipmentTag', name: 'Equipment Tag No' },
	{ id: 'equipmentDescription', name: 'Equipment Description' },
	{ id: 'supervisorName', name: 'Supervisor' },
	{ id: 'documentNumber', name: 'Document Number' },
	{ id: 'location', name: 'Location' },
	{ id: 'module', name: 'Module' },
];

export default ActivateProjectForm;
